// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

import clamp from "lodash-es/clamp";
import { ReactNode } from "react";

import { wrapIf } from "@/lib/utils";

import { searchMatchClassName, useSearchTerm } from "./search";

export type SearchedTextHighlighterProps = {
  text: string;
  /**
   * If you want to omit a part of the search term from the highlighting, you can provide it here.
   * This is useful if part of the search string is not included in the text to be highlighted.
   */
  omitFromSearchTerm?: string;
  className?: string;
  /**
   * Optional number to clip the text left and right, making sure the searchTerm match is always visible.
   * No matter what, the searchTerm match will always be visible, even if it exceeds maxChars.
   *
   * @default Infinity (= no clipping)
   */
  maxChars?: number;
  /**
   * Character to use to indicate that the text has been cut off.
   *
   * @default "…"
   */
  ellipsis?: ReactNode;
};

/**
 * Conditionally highlights parts of the text that match the search query within the sidebar search context.
 *
 * This version does not attempt auto-expansion, and through this avoids a lot of hook calls. Use it
 * for larger lists to improve the performance.
 */
export default function SearchedTextHighlighterWithoutAutoExpansion({
  text,
  omitFromSearchTerm,
  className,
  maxChars = Infinity,
  ellipsis = "…",
}: SearchedTextHighlighterProps) {
  const searchTerm = useSearchTerm();

  if (!searchTerm || !text) {
    return substringWithEllipsis(text, maxChars, ellipsis);
  }

  let searchText = searchTerm;

  if (omitFromSearchTerm) {
    searchText = searchTerm.replace(omitFromSearchTerm, "").trim();
  }

  const startIndex = text.toLowerCase().indexOf(searchText);

  if (startIndex === -1) {
    return substringWithEllipsis(text, maxChars, ellipsis);
  }

  const beforeString = text.substring(0, startIndex);
  const match = text.substring(startIndex, startIndex + searchText.length);
  const afterString = text.substring(startIndex + searchText.length);

  let before: ReactNode = beforeString;
  let after: ReactNode = afterString;

  if (text.length > maxChars) {
    const remainingMaxChars = maxChars - match.length;

    const beforePortion = clamp(
      beforeString.length / remainingMaxChars,
      0,
      0.5
    );

    const beforeTrimLength = Math.floor(remainingMaxChars * beforePortion);

    if (beforeString.length > beforeTrimLength) {
      before = (
        <>
          {ellipsis}
          {beforeString.substring(beforeString.length - beforeTrimLength)}
        </>
      );
    }

    const afterTrimLength = Math.floor(remainingMaxChars * (1 - beforePortion));

    if (afterString.length > afterTrimLength) {
      after = (
        <>
          {afterString.substring(0, afterTrimLength)}
          {ellipsis}
        </>
      );
    }
  }

  return (
    <span className={className}>
      {before}
      {/* manual string interpolation for efficiency’s sake */}
      <span
        className={`text-red-100 bg-red-500 ${searchMatchClassName}`}
      >
        {match}
      </span>
      {after}
    </span>
  );
}

function substringWithEllipsis(
  text: string,
  maxChars: number,
  ellipsis: ReactNode
): ReactNode {
  return wrapIf(text as ReactNode, text.length > maxChars, () => {
    return (
      <>
        {text.substring(0, maxChars)}
        {ellipsis}
      </>
    );
  });
}
